import { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';

/**
 * Mounts `children` into a separate React root, outside the app's <StrictMode> tree.
 *
 * react-plotly.js's Plot is a class component whose componentDidMount kicks off async
 * Plotly setup (geo/mapbox/gl3d subplots in particular) and whose componentWillUnmount
 * calls Plotly.purge(). Under StrictMode, React double-invokes mount->unmount->mount
 * synchronously against the *same* DOM node, and Plotly's async setup from the first
 * mount can still be settling when the purge+remount race it — corrupting internal
 * state (e.g. the "_scrollZoom" crash on geo subplots). Rendering into a fresh nested
 * root sidesteps that, but the nested root's own unmount can't happen synchronously
 * inside the outer root's effect cleanup (React warns about that) — so real unmount is
 * deferred, and if StrictMode's fake remount arrives before that deferred unmount fires,
 * it's simply cancelled and the existing root is reused rather than recreated. Net
 * result: Plot mounts exactly once for its real lifetime; StrictMode's fake cycle is
 * fully absorbed.
 *
 * Scoped to wherever it's used — the rest of the app keeps StrictMode's protection.
 */
function NoStrictModeBoundary({ children }) {
  const containerRef = useRef(null);
  const rootRef = useRef(null);
  const pendingUnmountRef = useRef(null);

  useLayoutEffect(() => {
    if (pendingUnmountRef.current) {
      clearTimeout(pendingUnmountRef.current);
      pendingUnmountRef.current = null;
    } else if (!rootRef.current) {
      rootRef.current = ReactDOM.createRoot(containerRef.current);
    }
    return () => {
      pendingUnmountRef.current = setTimeout(() => {
        pendingUnmountRef.current = null;
        if (rootRef.current) {
          rootRef.current.unmount();
          rootRef.current = null;
        }
      }, 0);
    };
  }, []);

  // Sync the latest children into the root on every render.
  useLayoutEffect(() => {
    if (rootRef.current) rootRef.current.render(children);
  });

  return <div ref={containerRef} style={{ width: '100%' }} />;
}

export default NoStrictModeBoundary;
